import { format, formatDuration, intervalToDuration, addSeconds, formatDistanceToNow,
    formatDistanceToNowStrict, startOfDay, isPast, parseISO, isAfter, differenceInDays  } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { zhCN, enUS, enGB, fr, de, id, ja, sk, es, bg, cs, hi, hr, hu, it, nl, pl, pt, ro, sv, tr, uk } from 'date-fns/locale'
import { getActiveLanguage } from 'laravel-vue-i18n'
import { ne } from '@/Composables/nepaliDateLocale'
import { ctrans } from '@/Composables/useTrans'

export const localesCode: any = {
    zhCN, enUS, enGB, fr, de, id, ja, sk, es,
    'zh-Hans': zhCN, en: enGB, bg, cs, hi, hr, hu, it, ne, nl, pl, pt, ro, sv, tr, uk,
}

export interface OptionsTime {
    formatTime?: string
    localeCode?: string
    keepTimezone?: boolean
    timeZone?: string
    addSuffix?: boolean
}


export const useFormatTime = (dateIso: string | Date | undefined, OptionsTime?: OptionsTime) => {
    if (!dateIso) return '-'  // If the provided data date is null

    let tempLocaleCode = OptionsTime?.localeCode ?? getActiveLanguage()
    tempLocaleCode = tempLocaleCode === 'zh-Hans' ? 'zhCN' : tempLocaleCode
    if (!localesCode[tempLocaleCode]) {
        tempLocaleCode = 'enGB'
    }

    let tempDateIso = OptionsTime?.keepTimezone
        ? new Date((typeof dateIso === 'string' ? dateIso : dateIso.toISOString()).replace('Z', ''))
        : new Date(dateIso)
    if (isNaN(tempDateIso.getTime())) return '-'
    if (OptionsTime?.formatTime === 'short-datetime') return format(tempDateIso, 'EEE, dd MMM yy HH:mm', { locale: localesCode[tempLocaleCode] })
    if (OptionsTime?.formatTime === 'aiku') return format(tempDateIso, 'EEE, do MMM yy', { locale: localesCode[tempLocaleCode] })  // Sun, 1st Jan 23
    if (OptionsTime?.formatTime === 'ddmy') return format(tempDateIso, 'PPPP', { locale: localesCode[tempLocaleCode] })  // Friday, April 29th, 1453
    if (OptionsTime?.formatTime === 'hms') return format(tempDateIso, 'PPpp', { locale: localesCode[tempLocaleCode] })  // Nov 2, 2023, 3:03:26 PM
    if (OptionsTime?.formatTime === 'hm') return format(tempDateIso, 'PPp', { locale: localesCode[tempLocaleCode] })  // Nov 2, 2023, 3:03 PM
    if (OptionsTime?.formatTime === 'mdy') return format(tempDateIso, 'PP', { locale: localesCode[tempLocaleCode] })  // Nov 2, 2023
    if (OptionsTime?.timeZone) {
        return formatInTimeZone(dateIso, OptionsTime.timeZone, OptionsTime?.formatTime || 'PPP', { locale: localesCode[tempLocaleCode] })
    }
    return format(tempDateIso, OptionsTime?.formatTime || 'PPP', { locale: localesCode[tempLocaleCode] }) // October 13th, 2023
}


// Relative time range (10 days ago)
export const useRangeFromNow = (dateIso: string | Date, OptionsTime?: OptionsTime) => {
    if (!dateIso) return '-'  // If the provided data date is null

    let tempLocaleCode = OptionsTime?.localeCode ?? getActiveLanguage()
    tempLocaleCode = tempLocaleCode === 'zh-Hans' ? 'zhCN' : tempLocaleCode
    if (!localesCode[tempLocaleCode]) {
        tempLocaleCode = 'enGB'
    }
    const date = new Date(dateIso)

    return formatDistanceToNow(date, { locale: localesCode[tempLocaleCode], includeSeconds: true, addSuffix: OptionsTime?.addSuffix ?? false })
}


// Range from today to expected date: "3 days left"
export const useDaysLeftFromToday = (isoDate?: string) => {
    if (!isoDate) return ctrans('No date')

    let targetDate: Date
    try {
        targetDate = new Date(isoDate)
        if (isNaN(targetDate.getTime())) {
            targetDate = parseISO(isoDate)
        }
    } catch {
        return ctrans('invalid date')
    }

    const today = startOfDay(new Date())
    targetDate = startOfDay(targetDate)

    if (!isAfter(targetDate, today)) {
        return ctrans('Date passed')
    }

    const daysLeft = differenceInDays(targetDate, today)

    if (daysLeft === 1) {
        return daysLeft + ' ' + ctrans('day left')
    } else if (daysLeft === 0) {
        return 'today'
    } else  {
        return daysLeft + ' ' + ctrans('days left')
    }
}

//estimated time left for retina
export const retinaUseDaysLeftFromToday = (isoDate?: string) => {
    if (!isoDate) return ctrans('No date')

    let targetDate: Date
    try {
        targetDate = new Date(isoDate)
        if (isNaN(targetDate.getTime())) {
            targetDate = parseISO(isoDate)
        }
    } catch {
        return ctrans('invalid date')
    }

    const today = startOfDay(new Date())
    targetDate = startOfDay(targetDate)

    if (!isAfter(targetDate, today)) {
        return ctrans('Date passed')
    }

    const daysLeft = differenceInDays(targetDate, today)

    if (daysLeft === 1) {
        return ctrans('in')  + ' ' + daysLeft + ' ' +  ctrans('days')
    } else if (daysLeft === 0) {
        return 'today'
    } else  {
        return ctrans('in') + ' ' + daysLeft + ' ' +  ctrans('days')
    }
}

// Time countdown 1
export const useTimeCountdown: any = (dateIso: string, options?: { human?: boolean, zero?: boolean }) => {
    if (!dateIso) return '-'  // If the provided data date is null

    const countdown = intervalToDuration({
        start: new Date(),
        end: new Date(dateIso)
    })

    if (isPast(new Date(dateIso))) return false  // If the provided date already passed then return false

    if (options?.human) return formatDuration(countdown, options)  // 5 days 23 hours 3 minutes 58 seconds

    return countdown  // { "years": 0, "months": 0, "days": 0, "hours": 0, "minutes": 51, "seconds": 0 }
}


// Time countdown 2
export const useSecondCountdown: any = (dateIso: string | Date, duration: number, options?: { human?: boolean, zero?: boolean }) => {
    if (!dateIso) return false  // If the provided data date is null

    const newDate = addSeconds(new Date(dateIso), duration)
    if (isPast(newDate)) return false
    return formatDistanceToNowStrict(newDate)  // 23 seconds
}


// Convert miliseconds to hours-minutes-seconds
export const useMilisecondToTime = (miliSecond: number) => {
    // Calculate minutes and seconds
    const hours = Math.floor(miliSecond / (1000 * 60 * 60))
    const minutes = Math.floor((miliSecond % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((miliSecond % (1000 * 60)) / 1000)

    // Format the result
    const formattedTime =
        (hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '') +
        (minutes > 0 ? `${hours > 0 ? ' ' : ''}${minutes} minute${minutes > 1 ? 's' : ''}` : '') +
        (seconds > 0
            ? `${(hours > 0 || minutes > 0)
                ? ' '
                : ''}
                ${seconds} second${seconds > 1
                ? 's'
                : ''}`
            : '0 second')

    return formattedTime // 2 hours 56 minutes 23 seconds
}


// Check if the provided date is a future date and does it already passed?
export const useIsFutureIsAPast = (dateIso: Date | string, additionalSeconds: number) => {
    if (!dateIso) return false  // If the provided data date is null

    const newDate = addSeconds(new Date(dateIso), additionalSeconds)

    return isPast(newDate)  // true or false
}


// Method: Convert from '28359' (in seconds) to '7:52:39'
export const useSecondsToMS = (seconds?: number) => {
    if (!seconds) return '00:00'

    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    const strHour = hours.toString()
    const strMinutes = minutes.toString().padStart(2, '0')
    const strSeconds = secs.toString().padStart(2, '0')

    return strHour + ':' + strMinutes + ':' + strSeconds
}


// Method: Convert date to '08:30 am'
export const useHMAP = (date?: string) => {
    if(!date) {
        return '-'
    }
    return format(parseISO(date), 'hh:mm a')
}

// Method: Check if a future date has passed (futureDate: '2025-07-04')
export const isFutureDatePassed = (futureDate: string) => {
    const today = new Date();
    const targetDate = new Date(futureDate);

    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    return targetDate < today;
}
